import { FC, useEffect, useState } from 'react';
import { useSetAtom } from 'jotai';
import { useForm } from '@tanstack/react-form';
import { useStore } from '@tanstack/react-store';
import { SidePanel } from '@carbon/ibm-products';
import { InlineNotification, TextInput } from '@carbon/react';
import type { StagedWell, WellListItem } from './types-well';
import { editWellAtom } from './atoms/wells';
import wellStyles from '../../afe-catalog/structuring/wells.module.scss';

// DO staged-well edit SidePanel. Clone of the AFE `well-edit-panel.tsx`, but
// re-scoped to the DO staged shape + the DO controller's editable blob keys.
//
// DO divergences from AFE:
//   • the DO staged well carries only wellName / uwiApi / county / state /
//     countyState / legalDescription / operator / propertyNumber / propertyName
//     / divisionOrderNumber — so this form drops every AFE-only field
//     (formation, basin, tvd/tmd/tll, shl/bhl/ftp/ltp, quarter/abstract/prospect);
//   • the DO controller's EDIT_BLOB_KEYS / DivisionOrderTarget::EDITABLE_KEYS permit
//     location_summary / locations / identifiers / additional_header_fields /
//     legal_descriptions — NOT `depths` (an AFE-only key). Sending a `depths`
//     blob would be silently dropped, so this panel never emits one;
//   • PATCHes .../division_order_structuring/wells/:id and hosts the SidePanel on
//     `.do-structuring-shell`.
//
// The staged fields map onto the DivisionOrderTarget `data` blobs the DO
// _staged_well jbuilder reads back:
//   Well Name           → well_name (scalar)
//   UWI / API Number    → identifiers.uwi
//   County / State      → location_summary.{county, state}
//   Operator            → additional_header_fields.operator (jbuilder prefers this,
//                         falls back to location_summary.operator)
//   Legal Description   → legal_descriptions[0].legal_description
//
// WellMatchable#edit_staged_fields! DEEP-MERGES the submitted leaves into the existing `data` and
// treats a null/blank leaf as "clear that leaf". So this panel sends the FULL
// set of managed leaves on every save (value or `null`) — never a subset:
//   * sending `null` lets the user actually CLEAR a wrongly-extracted value
//     (the old "include only non-empty keys" could never clear one);
//   * the deep-merge preserves unmanaged siblings the FE doesn't edit
//     (location_summary.county_state_raw, legal_descriptions[0].components),
//     so sending one leaf no longer clobbers the rest of its blob.

interface FormState {
  wellName: string;
  uwiApi: string;
  county: string;
  state: string;
  operator: string;
  legalDescription: string;
}

interface Props {
  open: boolean;
  documentId: number | string;
  well: WellListItem;
  staged: StagedWell;
  /** PATCH target — prefer an explicit href; fall back to the conventional URL. */
  editHref?: string;
  onClose: () => void;
  onSaved: () => void;
}

const blank = (value: string) => (value.trim() === '' ? null : value.trim());

// Pre-fill from the DO staged-well response. County/State arrive both combined
// (`countyState`) and split (`county`/`state`) — prefer the split fields.
const buildFormValues = (staged: StagedWell): FormState => ({
  wellName: staged.wellName ?? '',
  uwiApi: staged.uwiApi ?? '',
  county: staged.county ?? '',
  state: staged.state ?? '',
  operator: staged.operator ?? '',
  legalDescription: staged.legalDescription ?? '',
});

export const WellEditPanel: FC<Props> = ({
  open,
  documentId,
  well,
  staged,
  editHref,
  onClose,
  onSaved,
}) => {
  const editWell = useSetAtom(editWellAtom);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: buildFormValues(staged),
    onSubmit: ({ value }) => {
      const url =
        editHref ??
        `/documents/${documentId}/division_order_structuring/wells/${well.id}`;

      // Send the full set of managed leaves every save, `null` for any blanked
      // field, so the backend deep-merge can DISTINGUISH "unchanged" from
      // "cleared" and preserve unmanaged siblings. The DO legal-description key
      // is `legal_description` (the DocumentProcessor writes
      // legal_descriptions[].{legal_description, components}), NOT the AFE
      // full_legal_description/afe_legal_description keys.
      const wellPayload = {
        well_name: blank(value.wellName),
        identifiers: { uwi: blank(value.uwiApi) },
        location_summary: {
          county: blank(value.county),
          state: blank(value.state),
        },
        additional_header_fields: { operator: blank(value.operator) },
        legal_descriptions: [
          { legal_description: blank(value.legalDescription) },
        ],
      };

      setPending(true);
      setError(null);
      editWell({
        url,
        method: 'PATCH',
        data: { well: wellPayload },
        onSuccess: () => {
          setPending(false);
          onSaved();
        },
        onError: (m) => {
          setPending(false);
          setError(m ?? 'Failed to save well.');
        },
      });
    },
  });

  // Re-seed the form whenever the panel (re)opens for a well.
  useEffect(() => {
    if (open) {
      form.reset(buildFormValues(staged));
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, well.id]);

  // Live well name drives the panel title (updates as the user edits the field).
  const wellNameValue = useStore(form.store, (s) => s.values.wellName);

  return (
    <SidePanel
      open={open}
      onRequestClose={onClose}
      title={wellNameValue || staged.wellName || 'Edit Well'}
      subtitle="Edit the staged well details extracted from the document."
      className={wellStyles.panelLightFields}
      slideIn
      selectorPageContent=".do-structuring-shell"
      placement="right"
      size="md"
      actions={[
        { kind: 'secondary' as const, label: 'Cancel', onClick: onClose },
        {
          kind: 'primary' as const,
          label: pending ? 'Saving…' : 'Save Changes',
          disabled: pending,
          onClick: () => form.handleSubmit(),
        },
      ]}
    >
      {open && (
        <div className={wellStyles.editGrid}>
          {error && (
            <InlineNotification
              kind="error"
              lowContrast
              title="Error"
              subtitle={error}
              onCloseButtonClick={() => setError(null)}
            />
          )}
          <form.Field name="wellName">
            {(field) => (
              <TextInput
                id="do-well-edit-name"
                labelText="Well Name"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="uwiApi">
            {(field) => (
              <TextInput
                id="do-well-edit-uwi-api"
                labelText="UWI / API Number"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="county">
            {(field) => (
              <TextInput
                id="do-well-edit-county"
                labelText="County"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="state">
            {(field) => (
              <TextInput
                id="do-well-edit-state"
                labelText="State"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="operator">
            {(field) => (
              <TextInput
                id="do-well-edit-operator"
                labelText="Operator"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="legalDescription">
            {(field) => (
              <TextInput
                id="do-well-edit-legal-description"
                labelText="Legal Description"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
        </div>
      )}
    </SidePanel>
  );
};
